// Vertical-slice Iron Covenant buildings.

import type { BuildingDef } from './types';

export const BUILDINGS: BuildingDef[] = [
  {
    id: 'b-ic-crown-bastion',
    name: 'Crown Bastion',
    faction: 'iron-covenant',
    kind: 'core',
    maxHealth: 4500, // halved from bible 8,500 per owner feedback: assaults were too slow
    armor: 'moderate',
    footprint: { w: 8, h: 8 },
    isMajorStructure: true,
    buildTime: 0,
    placement: 'core-territory',
    attack: {
      damage: 180,
      period: 4.5,
      range: 24,
      projectileSpeed: 20,
      damageVs: { large: 1.3, giant: 1.5 },
      targetPriority: ['giant', 'large', 'siege', 'squad'],
    },
    // Core Annex System (owner spec 2026-07-13): three permanent sockets, all
    // eventually buildable — the strategic choice is build ORDER. Replaces the
    // v0.2 Reinforced Bastion / Royal Workshop / Command Hall track (README Deviations).
    upgrades: [
      {
        id: 'up-ic-high-command-hall',
        name: 'High Command Hall',
        icon: '⚔',
        socket: 'top',
        cost: { power: 280, essence: 35 }, // spec
        buildTime: 15, // owner request 2026-07-13
        rulesText:
          'Production buildings train 20% faster; fresh squads march 10% faster for 12 s. Prepares a free Reserve Cohort every 120 s (max 2, costs 2 Command).',
        effects: [
          { kind: 'trainSpeedMult', mult: 1.2 }, // spec
          { kind: 'freshSquadSpeed', mult: 1.1, duration: 12 }, // spec
          // spec's "2 s shorter auto-queue delay" dropped: auto-produce refills
          // instantly in this build — see README Deviations
        ],
        spawner: { unitId: 'u-ic-reserve-cohort', period: 120, maxAlive: 2 }, // spec
      },
      {
        id: 'up-ic-royal-works',
        name: 'Royal Works',
        icon: '⚒',
        socket: 'left',
        cost: { power: 250, essence: 20 }, // spec
        buildTime: 15, // owner request 2026-07-13
        rulesText:
          '+3 Workforce. Buildings construct 25% faster and upgrades 20% faster; builders take 25% less damage, unfinished buildings gain scaffold armor, and a fifth worker may join each build.',
        effects: [
          { kind: 'workforceBonus', amount: 3 }, // spec
          { kind: 'buildSpeedMult', mult: 1.25 }, // spec
          { kind: 'upgradeSpeedMult', mult: 1.2 }, // spec
          { kind: 'builderProtection', mult: 0.75 }, // spec
          { kind: 'scaffoldProtection', mult: 0.85 }, // spec "scaffold armor" // TEST
          { kind: 'extraBuilders', amount: 1 }, // spec: up to five workers
        ],
      },
      {
        id: 'up-ic-marching-foundry',
        name: 'Marching Foundry',
        icon: '🏗',
        socket: 'bottom',
        cost: { power: 320, essence: 45 }, // spec
        buildTime: 15, // owner request 2026-07-13
        rulesText:
          'Produces a Bastion Wagon every 135 s. E: Establish Field Bastion unfolds it into a free forward fort with a ballista, summoning ground, and work auras.',
        spawner: { unitId: 'u-ic-bastion-wagon', period: 135, maxAlive: 1 }, // spec
      },
    ],
    ui: { color: 0x9aa4b8, label: 'Crown Bastion' },
  },
  {
    // Core Annex System: free forward fort unfolded from a Bastion Wagon. Strong
    // enough to support a push, not to take a site alone. Worker shelter (2)
    // dropped — shelter exists only at the Core (README Deviations).
    id: 'b-ic-field-bastion',
    name: 'Field Bastion',
    faction: 'iron-covenant',
    kind: 'defense',
    maxHealth: 1600, // TEST
    armor: 'moderate',
    footprint: { w: 5, h: 5 },
    isMajorStructure: false,
    buildTime: 60, // spec: automatically builds for 60 s
    placement: 'both',
    selfBuilding: true, // spec: free deployed buildings need no workers
    summonAnchor: { maxAscension: 2, radius: 14 }, // spec: Iron summoning/recovery location
    passive: { kind: 'trainSpeedAura', radius: 18, mult: 1.1 }, // spec: +10% training nearby
    passives: [
      { kind: 'constructionAura', radius: 18, buildMult: 1.15 }, // spec: +15% construction speed
      { kind: 'squadRecoveryAura', radius: 18, mult: 1.25 }, // spec "recovery location" // TEST
    ],
    attack: {
      damage: 95, // spec: light anti-large ballista // TEST
      period: 2.6,
      range: 16,
      projectileSpeed: 20,
      damageVs: { large: 1.5, giant: 1.6 },
      targetPriority: ['giant', 'large', 'siege', 'squad'],
    },
    ui: { color: 0x8494aa, label: 'Field Bastion' },
  },
  {
    id: 'b-ic-barracks',
    name: 'Bastion Barracks',
    faction: 'iron-covenant',
    kind: 'production',
    maxHealth: 1600, // bible IC-05
    armor: 'moderate',
    footprint: { w: 5, h: 5 },
    isMajorStructure: true,
    buildTime: 34,
    placement: 'both',
    signatureText: 'Ordered Muster: after 20 s without damage, the next squad trains faster and marches swiftly to its rally point.',
    traits: [
      { kind: 'safeMuster', safeSeconds: 20, trainMult: 1.2, marchMult: 1.18, marchSeconds: 12 },
      { kind: 'doctrine', offUnitId: 'u-ic-shield-legion', onUnitId: 'u-ic-pike-wardens' },
    ],
    roster: {
      queueSize: 5,
      entries: [
        {
          unitId: 'u-ic-pike-wardens',
          cost: { power: 120, essence: 0, command: 3 },
          trainTime: 27,
          ascension: 2,
        },
        {
          // bible §7.1 roster matrix: the Banner Captain drills at the Barracks
          unitId: 'u-ic-shield-legion',
          cost: { power: 95, essence: 0, command: 3 },
          trainTime: 24,
          ascension: 1,
        },
        // v0.2.4 function-based roster (Iron Bastion IC-18 analog): line & cavalry wing
      ],
    },
    upgrade: {
      id: 'up-ic-veteran-commandery',
      name: 'Veteran Commandery',
      cost: { power: 180, essence: 35 },
      rulesText: 'Toggle Defensive or Anti-Large doctrine to prioritize Shield Legion or Pike Wardens during auto-production.',
    },
    ui: { color: 0x7d8aa0, label: 'Barracks' },
  },
  {
    id: 'b-ic-longshot-foundry',
    name: 'Longshot Foundry',
    faction: 'iron-covenant',
    kind: 'production',
    maxHealth: 1450, // bible IC-06
    armor: 'light',
    footprint: { w: 5, h: 5 },
    isMajorStructure: true,
    buildTime: 45,
    placement: 'both',
    signatureText: 'Calibration Cycle: every 35 s, marks the most valuable visible enemy for allied precision and siege fire.',
    traits: [{ kind: 'periodicMark', radius: 24, cooldown: 35, duration: 8, markId: 'building-mark' }],
    roster: {
      queueSize: 5,
      entries: [
        {
          unitId: 'u-ic-longshot-snipers',
          cost: { power: 155, essence: 25, command: 4 },
          trainTime: 34,
          ascension: 2,
        },
        // v0.2.4 function-based roster (Longshot Foundry IC-19 analog): precision fire
      ],
    },
    upgrade: {
      id: 'up-ic-perfect-calibration',
      name: 'Perfect Calibration',
      cost: { power: 220, essence: 60 },
      // range buffs are unsupported; produced units gain +10% health instead (deviation)
      rulesText: 'Rangefinder Array improves Calibration Cycle and enables long-range target designation.',
    },
    ui: { color: 0x8a9678, label: 'Foundry' },
  },
  {
    id: 'b-ic-builder-guild',
    name: 'Royal Builder Guild',
    faction: 'iron-covenant',
    kind: 'production',
    maxHealth: 1250, // bible IC-07
    armor: 'light',
    footprint: { w: 4, h: 4 },
    isMajorStructure: true,
    buildTime: 30,
    placement: 'both',
    dropOff: ['power'],
    passive: { kind: 'constructionAura', radius: 18, buildMult: 1.2 },
    signatureText: 'Emergency Shoring: once per minute, a nearby structure below 35% health gains temporary scaffold armor.',
    traits: [
      { kind: 'emergencyShoring', radius: 18, threshold: 0.35, cooldown: 60, shield: 300, duration: 8 },
      { kind: 'prefabrication', cooldown: 90, startingProgress: 0.3, upgradeOnly: true },
    ],
    roster: {
      queueSize: 3,
      entries: [
        {
          unitId: 'u-ic-master-builder',
          cost: { power: 100, essence: 0 },
          trainTime: 30,
          ascension: 2,
        },
      ],
    },
    upgrade: {
      id: 'up-ic-prefabricated-stores',
      name: 'Prefabricated Stores',
      cost: { power: 150, essence: 0 },
      rulesText: 'The first wall or defensive tower started every 90 s begins 30% complete.',
    },
    ui: { color: 0xb09a6a, label: 'Builder Guild' },
  },
  {
    id: 'b-ic-siege-depot',
    name: 'Siege Depot',
    faction: 'iron-covenant',
    kind: 'production',
    maxHealth: 2000, // bible IC-08
    armor: 'moderate',
    footprint: { w: 6, h: 6 },
    isMajorStructure: true,
    buildTime: 52,
    placement: 'both',
    passive: { kind: 'largeRecoveryAura', radius: 20, mult: 1.35 },
    signatureText: 'Ammunition Crane periodically empowers nearby siege pieces while the Depot accelerates recovery.',
    traits: [{ kind: 'periodicSquadBuff', radius: 20, cooldown: 100, duration: 18, damageMult: 1.3, role: 'siege' }],
    roster: {
      queueSize: 4,
      entries: [
        {
          unitId: 'u-ic-mobile-ballista',
          cost: { power: 210, essence: 35, command: 6 },
          trainTime: 40, // TEST
          ascension: 3,
        },
        // v0.2.4 function-based roster (Royal Siege Depot IC-20 analog): siege arsenal
        // and engineering sustain
      ],
    },
    upgrade: {
      id: 'up-ic-royal-siege-license',
      name: 'Royal Siege License',
      cost: { power: 250, essence: 90 },
      rulesText: 'Ammunition payloads also deal bonus damage to Giants and fortified structures.',
    },
    ui: { color: 0x74808c, label: 'Siege Depot' },
  },
  {
    // Visible defensive trap-structure (bible IC-21): marks the first squads that
    // force passage; collapses forward with a stun when destroyed.
    id: 'b-ic-oathbound-gate',
    name: 'Oathbound Gate',
    faction: 'iron-covenant',
    kind: 'trap-structure',
    maxHealth: 1100, // bible IC-21
    armor: 'heavy',
    footprint: { w: 2, h: 8 },
    isMajorStructure: false,
    buildTime: 20,
    placement: 'both',
    selfBuilding: true,
    gate: { marks: 3, markDuration: 10, damageBonus: 1.2, stunSeconds: 1.5, radius: 6 },
    ui: { color: 0x9a8a74, label: 'Oathbound Gate' },
  },
  {
    // Rises automatically when a site is captured; destroying it neutralizes the site.
    id: 'b-ic-site-tower',
    name: 'Site Tower',
    faction: 'iron-covenant',
    kind: 'defense',
    maxHealth: 1500,
    armor: 'moderate',
    footprint: { w: 3, h: 3 },
    isMajorStructure: false,
    buildTime: 0,
    placement: 'site-radius',
    producesWorkers: true,
    isSiteTower: true,
    signatureText: 'Alarm Muster: squads ordered to defend this site hurry toward its capture radius.',
    traits: [{ kind: 'defendHaste', moveMult: 1.2, siteRadius: 14 }],
    attack: {
      damage: 34,
      period: 1.1,
      range: 15,
      projectileSpeed: 24,
      targetPriority: ['squad', 'worker', 'large', 'giant'],
    },
    attackRequiresUpgrade: true,
    upgrade: {
      id: 'up-ic-tower-armaments',
      name: 'Tower Armaments',
      cost: { power: 150, essence: 0 },
      rulesText: 'Arms the tower with an arbalest that automatically fires at nearby enemies.',
    },
    ui: { color: 0x8fa3c0, label: 'Site Tower' },
  },
  {
    id: 'b-ic-bulwark-arbalest',
    name: 'Bulwark Arbalest',
    faction: 'iron-covenant',
    kind: 'defense',
    maxHealth: 1250, // TEST
    armor: 'moderate',
    footprint: { w: 3, h: 3 },
    isMajorStructure: true,
    buildTime: 32, // TEST
    placement: 'both',
    signatureText: 'Brace Shot: after 12 s undamaged, the next armor-piercing bolt hits hard and applies Giant Mark.',
    traits: [{ kind: 'braceShot', safeSeconds: 12, damageMult: 1.8, markId: 'giant-mark', markSeconds: 8 }],
    attack: {
      damage: 105, // TEST
      period: 2.8,
      range: 19,
      projectileSpeed: 24,
      damageVs: { large: 1.5, giant: 1.8 },
      targetPriority: ['giant', 'large', 'siege', 'squad'],
    },
    upgrade: {
      id: 'up-ic-twin-winch',
      name: 'Twin Winch',
      cost: { power: 190, essence: 35 }, // TEST
      rulesText: 'Brace Shot also fires a weaker second bolt at another heavy target.',
    },
    ui: { color: 0x8797ad, label: 'Bulwark Arbalest' },
  },
  {
    // Destructible channel anchor for rituals (§19.7). HP is overridden per ritual.
    id: 'b-ic-ritual-standard',
    name: 'Ritual Standard',
    faction: 'iron-covenant',
    kind: 'defense',
    maxHealth: 500,
    armor: 'none',
    footprint: { w: 2, h: 2 },
    isMajorStructure: false,
    buildTime: 0,
    placement: 'both',
    ui: { color: 0xd8c07a, label: 'Ritual Standard' },
  },
  {
    // Claimed from ruins; blocks its choke; up to 2 ranged squads can mount the battlements.
    id: 'b-ic-castle-wall',
    name: 'Castle Wall',
    faction: 'iron-covenant',
    kind: 'wall',
    maxHealth: 1400,
    armor: 'heavy',
    footprint: { w: 2, h: 12 },
    isMajorStructure: false,
    buildTime: 30, // TEST — owner: walls take a while to raise and are fragile mid-build
    placement: 'both',
    selfBuilding: true,
    mountSlots: 2,
    ui: { color: 0x9c948a, label: 'Castle Wall' },
  },
];
